import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Container,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import Body from '../components/Body';
import { track } from '../helpers/mixpanel';
import UserPresentationByTokenQuery from '../data/queries/UserPresentationByTokenQuery';
import UpdateUserPresentationDecisionMutation from '../data/mutations/UpdateUserPresentationDecisionMutation';
import ProfilePhotoGallery from '../components/ProfilePhotoGallery';
import MotivationModal from '../components/MotivationModal';
import SuggestedTimesDisplay from '../components/SuggestedTimesDisplay';
import TimeSelectionModal from '../components/TimeSelectionModal';
import MatchCelebrationDialog from '../components/MatchCelebrationDialog';

const spinner = { 
  height: '100vh', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center' 
};

export default function UserPresentation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signedToken = searchParams.get('token');
  const isTimeOnlyMode = searchParams.get('mode') === 'time'; // Check for time-only mode
  const [modalOpen, setModalOpen] = useState(false);
  const [decision, setDecision] = useState<boolean | null>(null);
  const [timeSelectionModalOpen, setTimeSelectionModalOpen] = useState(false);
  const [showMatchCelebration, setShowMatchCelebration] = useState(false);
  const [selectedTimeFromList, setSelectedTimeFromList] = useState<string | null>(null);
  
  // Use presentation query with token
  const { data: presentationData, loading: presentationLoading, error: presentationError } = useQuery(UserPresentationByTokenQuery, { 
    fetchPolicy: 'cache-and-network',
    variables: { token: signedToken },
    skip: !signedToken
  });

  const [updateDecision] = useMutation(UpdateUserPresentationDecisionMutation);

  const presentation = presentationData?.userPresentationByToken;

  // Determine current state based on user choices
  const getCoordinationState = () => {
    if (!presentation) return 'loading';

    console.log(presentation)

    // Check if both parties have agreed on a time
    const decidedTimes = presentation.decidedTimes || [];
    console.log('Decided times check:', {
      decidedTimes,
      count: decidedTimes.length,
      hasDecidedTimes: decidedTimes.length > 0
    });
    
    // TEMPORARY TEST: Simulate decided times for testing UI
    // Remove this after testing
    const hasTestDecidedTimes = false; // Set to true to test the decided time UI
    if (hasTestDecidedTimes || decidedTimes.length > 0) {
      return 'time_decided';
    }

    const decision = presentation.decision;
    const readerDecision = presentation.readerDecision;

    // No decisions made yet
    if (!decision?.accept && !readerDecision?.accept) {
      return 'initial';
    }

    // Presentation owner accepted, reader hasn't decided yet
    if (decision?.accept && !readerDecision?.accept) {
      return 'decision_accepted_waiting_reader';
    }

    // Reader accepted, presentation owner hasn't decided yet
    if (readerDecision?.accept && !decision?.accept) {
      return 'reader_accepted_waiting_decision';
    }

    // Both accepted - now coordinate times
    if (decision?.accept && readerDecision?.accept) {
      const decisionTimes = decision.suggestedTimes || [];
      const readerTimes = readerDecision.suggestedTimes || [];

      // No times suggested by either
      if (decisionTimes.length === 0 && readerTimes.length === 0) {
        return 'both_accepted_no_times';
      }

      // If both parties have made decisions (regardless of current suggestedTimes), 
      // check timestamps to determine who should respond
      if (decision.decisionAt && readerDecision.decisionAt) {
        const decisionTime = new Date(decision.decisionAt);
        const readerTime = new Date(readerDecision.decisionAt);
        
        console.log('Timestamp comparison:', {
          decisionTime: decisionTime.toISOString(),
          readerTime: readerTime.toISOString(),
          readerMoreRecent: readerTime > decisionTime,
          decisionTimesCount: decisionTimes.length,
          readerTimesCount: readerTimes.length
        });
        
        if (readerTime > decisionTime) {
          // Reader suggested more recently, so they're waiting for decision owner's response
          return 'waiting_for_decision_response';
        } else if (decisionTime > readerTime) {
          // Decision owner suggested more recently, so they're waiting for reader's response
          return 'waiting_for_reader_response';
        }
      }

      // Fallback to simple time counting (for initial suggestions)
      // Only presentation owner suggested times
      if (decisionTimes.length > 0 && readerTimes.length === 0) {
        return 'decision_suggested_times';
      }

      // Only reader suggested times
      if (readerTimes.length > 0 && decisionTimes.length === 0) {
        return 'reader_suggested_times';
      }
    }

    return 'initial';
  };

  const coordinationState = getCoordinationState();

  // Check if the current reader has already made a decision
  const readerHasDecided = !!presentation?.readerDecision?.accept || presentation?.readerDecision?.accept === false;
  const readerCurrentDecision = presentation?.readerDecision?.accept;
  const readerMotivation = presentation?.readerDecision?.motivation;
  const readerSuggestedTimes = presentation?.readerDecision?.suggestedTimes || [];

  console.log('Coordination State:', coordinationState);
  console.log('Reader has decided:', readerHasDecided, 'Decision:', readerCurrentDecision);
  
  // Determine if current viewer should see time coordination UI
  const shouldShowTimeCoordination = () => {
    return coordinationState === 'both_accepted_no_times' ||
           coordinationState === 'decision_suggested_times' ||
           coordinationState === 'reader_suggested_times' ||
           coordinationState === 'waiting_for_decision_response' ||
           coordinationState === 'waiting_for_reader_response' ||
           coordinationState === 'time_decided' ||
           isTimeOnlyMode;
  };

  useEffect(() => {
    if (signedToken && presentation) {
      track('view_profile_summary', { 
        presentationId: presentation.presentationId, 
        signedToken 
      });
    }
  }, [signedToken, presentation]);

  // Auto-open time selection modal in time-only mode
  useEffect(() => {
    if (isTimeOnlyMode && presentation && !modalOpen) {
      setDecision(true); // Always accepting in time-only mode
      setModalOpen(true);
    }
  }, [isTimeOnlyMode, presentation, modalOpen]);

  const handleDecision = useCallback((type: boolean) => {
    setDecision(type);
    
    // Check if the other party has already accepted with suggested times
    const decisionTimes = presentation?.decision?.suggestedTimes || [];
    const readerTimes = presentation?.readerDecision?.suggestedTimes || [];
    const decisionAccepted = presentation?.decision?.accept;
    const readerAccepted = presentation?.readerDecision?.accept;
    const rejectedTimes = presentation?.rejectedTimes || [];
    
    // If this is an edit of an existing decision, skip celebrations
    const isEditingExistingDecision = readerHasDecided;
    
    // If accepting and the other party has already accepted, show celebration first
    // BUT only if this is a NEW decision, not an edit
    if (type && !isEditingExistingDecision) {
      // If this reader is accepting for the first time and the presentation owner already accepted
      if (!readerAccepted && decisionAccepted) {
        setShowMatchCelebration(true);
      }
      // If this is a presentation owner accepting for the first time and reader already accepted
      else if (!decisionAccepted && readerAccepted) {
        setShowMatchCelebration(true);
      }
      // Otherwise show regular motivation modal
      else {
        setModalOpen(true);
      }
    } else {
      // If declining or editing existing decision, show motivation modal directly
      setModalOpen(true);
    }
    
    track(type ? 'accept_profile' : 'decline_profile', { 
      presentationId: presentation?.presentationId, 
      signedToken
    });
  }, [presentation, readerHasDecided, signedToken]);

  const handleMotivationClose = useCallback(() => {
    setModalOpen(false);
    setDecision(null);
    navigate('/thank-you?decision=cancel');
  }, [navigate]);

  const handleMotivationCancel = useCallback(() => {
    setModalOpen(false);
    setDecision(null);
    // Don't navigate - just close the modal
  }, []);

  const handleMotivationSubmit = useCallback(async (motivation: string, suggestedTimes?: any[]) => {
    try {
      // Submit decision with presentation data
      if (presentation?.presentationId && decision !== null) {
        await updateDecision({
          variables: {
            presentationId: presentation.presentationId,
            decision: decision,
            motivation: motivation || null,
            suggestedTimes: suggestedTimes || null
          },
          refetchQueries: [
            {
              query: UserPresentationByTokenQuery,
              variables: { token: signedToken }
            }
          ]
        });
      }
      
      track('submit_motivation', { 
        presentationId: presentation?.presentationId, 
        signedToken,
        decision, 
        hasMotivation: !!motivation,
        hasSuggestedTimes: !!(suggestedTimes && suggestedTimes.length > 0)
      });
    } catch (error) {
      console.error('Error updating decision:', error);
    }
    
    // Create navigation URL based on decision and whether times were provided
    const hasSelectedDates = !!(suggestedTimes && suggestedTimes.length > 0);
    const uniqueDatesCount = hasSelectedDates ? new Set(suggestedTimes.map(time => time.date)).size : 0;
    
    let navigationUrl = '/thank-you?';
    if (decision) {
      navigationUrl += `decision=accept&dates=${uniqueDatesCount}`;
    } else {
      navigationUrl += 'decision=decline';
    }
    
    setModalOpen(false);
    setDecision(null);
    navigate(navigationUrl);
  }, [presentation?.presentationId, decision, updateDecision, navigate, signedToken]);

  // Handle time coordination actions
  const handleTimeCoordination = useCallback(() => {
    if (coordinationState === 'both_accepted_no_times') {
      // No existing times, show regular time picker
      setDecision(true);
      setModalOpen(true);
    } else {
      // There are existing suggested times, show selection modal
      setTimeSelectionModalOpen(true);
    }
  }, [coordinationState]);

  const handleMatchCelebrationContinue = useCallback(() => {
    setShowMatchCelebration(false);
    
    // Check if the other party has suggested times - don't submit acceptance yet
    const decisionTimes = presentation?.decision?.suggestedTimes || [];
    const readerTimes = presentation?.readerDecision?.suggestedTimes || [];
    const decisionAccepted = presentation?.decision?.accept;
    const readerAccepted = presentation?.readerDecision?.accept;
    const rejectedTimes = presentation?.rejectedTimes || [];
    
    // Determine if we should show time selection or regular modal
    if (!readerAccepted && decisionAccepted && decisionTimes.length > 0) {
      // Reader accepting, presentation owner has times
      setTimeSelectionModalOpen(true);
    } else if (!decisionAccepted && readerAccepted && readerTimes.length > 0) {
      // Presentation owner accepting, reader has times
      setTimeSelectionModalOpen(true);
    } else {
      // No times yet, show regular motivation modal
      setModalOpen(true);
    }
  }, [presentation]);

  const handleTimeSelection = useCallback(async (selectedTime?: any, newSuggestedTimes?: any[]) => {
    try {
      if (presentation?.presentationId && decision !== null) {
        if (selectedTime) {
          // User selected an existing suggested time - submit acceptance with selected time
          await updateDecision({
            variables: {
              presentationId: presentation.presentationId,
              decision: decision, // Use the stored decision (should be true)
              motivation: null,
              suggestedTimes: [selectedTime]
            },
            refetchQueries: [
              {
                query: UserPresentationByTokenQuery,
                variables: { token: signedToken }
              }
            ]
          });
          
          track('accept_with_time_selected', { 
            presentationId: presentation.presentationId,
            selectedTime: selectedTime,
            signedToken
          });
          
          // Format the selected time for URL
          const timeParam = encodeURIComponent(selectedTime);
          navigate(`/thank-you?decision=time_decided&selectedTime=${timeParam}`);
        } else if (newSuggestedTimes) {
          // User suggested new times - submit acceptance with new suggested times
          await updateDecision({
            variables: {
              presentationId: presentation.presentationId,
              decision: decision, // Use the stored decision (should be true)
              motivation: null,
              suggestedTimes: newSuggestedTimes
            },
            refetchQueries: [
              {
                query: UserPresentationByTokenQuery,
                variables: { token: signedToken }
              }
            ]
          });
          
          track('accept_with_new_times_suggested', { 
            presentationId: presentation.presentationId,
            timesCount: newSuggestedTimes.length,
            signedToken
          });
          
          navigate('/thank-you?decision=times_suggested');
        }
      }
    } catch (error) {
      console.error('Error updating decision with time selection:', error);
    }
    
    setTimeSelectionModalOpen(false);
    setDecision(null); // Clear the stored decision
  }, [presentation?.presentationId, decision, updateDecision, navigate, signedToken]);

  // Show error if no valid token or if data not found
  if (!signedToken || presentationError || (!presentationLoading && !presentation)) {
    // Determine error type from GraphQL error
    const errorCode = presentationError?.graphQLErrors?.[0]?.extensions?.code;
    const errorMessage = presentationError?.graphQLErrors?.[0]?.extensions?.message;
    
    let title = t('profile_summary.invalid_link', 'Ogiltig länk');
    let message = t('profile_summary.link_expired', 'Denna länk är ogiltig eller har utgått.');
    
    if (errorCode === 'EXPIRED_TOKEN') {
      title = t('profile_summary.expired_token', 'Länken har utgått');
      message = t('profile_summary.expired_token_message', 'Denna länk har gått ut. Be om en ny länk.');
    } else if (errorCode === 'NO_ACCESS') {
      title = t('profile_summary.no_access', 'Ingen behörighet');
      message = t('profile_summary.no_access_message', 'Du har inte behörighet att visa denna profil.');
    } else if (errorCode === 'NOT_FOUND') {
      title = t('profile_summary.not_found', 'Profilen hittades inte');
      message = t('profile_summary.not_found_message', 'Denna profil finns inte längre.');
    }
    
    return (
      <Body>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {errorMessage || message}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.3)',
                  border: '1px solid rgba(255,255,255,0.5)'
                }
              }}
            >
              {t('common.back_to_home', 'Tillbaka till startsidan')}
            </Button>
          </Box>
        </Container>
      </Body>
    );
  }

  if (presentationLoading) {
    return (
      <Box sx={spinner}>
        <CircularProgress />
      </Box>
    );
  }


  const fullName = presentation?.firstName || t('common.anonymous', 'Anonym');
  
  // Convert height from meters to cm if needed
  const height = presentation?.height 
    ? (presentation.height < 3 ? `${Math.round(presentation.height * 100)} cm` : `${presentation.height} cm`)
    : null;

  // Get profile pictures from presentation selectedPhotos
  const profilePictures = presentation?.selectedPhotos?.map((photo: any) => photo.uri).filter(Boolean) || [];

  // Render time coordination view when appropriate
  if (shouldShowTimeCoordination()) {
    const decisionTimes = presentation?.decision?.suggestedTimes || [];
    const readerTimes = presentation?.readerDecision?.suggestedTimes || [];
    const rejectedTimes = presentation?.rejectedTimes || [];
    
    // Determine which times to show based on who we are and what the state is
    const getTimesToShow = () => {
      if (coordinationState === 'decision_suggested_times') {
        // Show presentation owner's times to reader for selection
        return { times: decisionTimes, suggestedBy: 'decision' as const, suggestedByName: presentation?.firstName, showWaitingMessage: false };
      } else if (coordinationState === 'reader_suggested_times') {
        // Show reader's times to presentation owner for selection  
        // Note: This shouldn't happen since presentation is always viewed by reader
        return { times: readerTimes, suggestedBy: 'reader' as const, suggestedByName: 'Du', showWaitingMessage: false };
      } else if (coordinationState === 'waiting_for_decision_response') {
        // Reader made suggestions and is waiting for decision owner to respond
        // Show reader's own suggestions with waiting message
        return { times: readerTimes, suggestedBy: 'reader' as const, suggestedByName: undefined, showWaitingMessage: true, waitingFor: 'decision', isViewingOwnSuggestions: true };
      } else if (coordinationState === 'waiting_for_reader_response') {
        // Decision owner made suggestions and reader (viewer) needs to respond  
        // Show decision owner's suggestions for reader to select from (no waiting message)
        return { times: decisionTimes, suggestedBy: 'decision' as const, suggestedByName: presentation?.firstName, showWaitingMessage: false };
      }
      return { times: [], suggestedBy: 'decision' as const, suggestedByName: undefined, showWaitingMessage: false };
    };

    const { times, suggestedBy, suggestedByName, showWaitingMessage, waitingFor, isViewingOwnSuggestions } = getTimesToShow();
    
    console.log('UI State Debug:', {
      coordinationState,
      showWaitingMessage,
      isViewingOwnSuggestions,
      suggestedBy,
      suggestedByName,
      timesCount: times.length
    });
    
    return (
      <>
        <Helmet>
          <title>{t('profile_summary.time_coordination', 'Tidskoordinering')} - Relate</title>
          <meta name="description" content={t('profile_summary.time_coordination_description', 'Koordinera tider för träff')} />
        </Helmet>
        
        <Body>
          <Container maxWidth="md" sx={{ py: 4 }}>
            
            {/* Profile Photo Gallery */}
            <ProfilePhotoGallery pictures={profilePictures} name={fullName} />

            {/* Name and Coordination Message */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h1" gutterBottom sx={{ color: 'white', mb: 2 }}>
                {fullName || t('common.anonymous', 'Anonym')}
              </Typography>
              
              <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
                {coordinationState === 'both_accepted_no_times' && t('profile_summary.coordinate_times', 'Föreslå tider som passar dig för er träff')}
                {coordinationState === 'decision_suggested_times' && t('profile_summary.decision_suggested_times', 'Välj en tid som passar dig')}
                {coordinationState === 'reader_suggested_times' && t('profile_summary.reader_suggested_times', 'Välj en tid som passar dig')}
                {coordinationState === 'waiting_for_reader_response' && t('profile_summary.waiting_for_reader_response', 'Välj en tid som passar dig')}
                {coordinationState === 'waiting_for_decision_response' && t('profile_summary.waiting_for_decision_response', 'Du föreslog tider')}
                {coordinationState === 'both_suggested_times' && t('profile_summary.choose_from_other_times', 'Välj en av tiderna som passar dig')}
                {coordinationState === 'time_decided' && t('profile_summary.time_decided', 'Träffen är bestämd!')}
              </Typography>
            </Box>

            {/* Show decided time(s) when both parties have agreed */}
            {(() => {
              const shouldShow = coordinationState === 'time_decided' && presentation?.decidedTimes && presentation.decidedTimes.length > 0;
              console.log('Should show decided time UI:', {
                coordinationState,
                isTimeDecided: coordinationState === 'time_decided',
                hasDecidedTimes: presentation?.decidedTimes?.length > 0,
                decidedTimesArray: presentation?.decidedTimes,
                shouldShow
              });
              return shouldShow;
            })() && (
              <Box sx={{ mt: 6, mb: 6, textAlign: 'center' }}>
                {/* Time display exactly like ThankYou page */}
                <Box sx={{ mb: 4 }}>
                  {presentation.decidedTimes.map((time: string, index: number) => {
                    const date = new Date(time);
                    const weekday = date.toLocaleDateString('sv-SE', { weekday: 'long' });
                    const day = date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' });
                    const timeStr = date.toLocaleTimeString('sv-SE', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: false 
                    });
                    
                    const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                    const formattedTime = `${capitalizedWeekday}, ${day} kl. ${timeStr}`;
                    
                    return (
                      <Typography 
                        key={index}
                        variant="body1" 
                        sx={{ 
                          fontSize: '1.2rem',
                          color: 'rgba(255,255,255,0.9)',
                          mb: 2
                        }}
                        dangerouslySetInnerHTML={{ __html: `📅 <strong>${formattedTime}</strong>` }}
                      />
                    );
                  })}
                </Box>

                {/* Simple next steps */}
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  mb: 3,
                  fontSize: '1.1rem',
                  lineHeight: 1.5,
                  maxWidth: '500px',
                  margin: '0 auto'
                }}>
                  Vi har bekräftat er träff och kommer att skicka ett meddelande till den andra personen. Ni kommer båda att få information om tid och plats för er träff inom kort!
                </Typography>
                
              </Box>
            )}

            {/* Show times - either for selection or for waiting feedback */}
            {times.length > 0 && coordinationState !== 'time_decided' && (
              <>
                {/* Show waiting message - only when you're actually waiting for their response */}
                {(showWaitingMessage && isViewingOwnSuggestions) ? (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#8E603C', fontWeight: 'bold', mb: 2 }}>
                      {waitingFor === 'decision' 
                        ? t('time_coordination.waiting_for_decision_name', `Väntar på svar från ${presentation?.firstName}`)
                        : t('time_coordination.waiting_for_reader_name', `Väntar på svar från ${suggestedByName || presentation?.firstName}`)}
                    </Typography>
                  </Box>
                ) : null}
                
                <SuggestedTimesDisplay
                  suggestedTimes={times}
                  suggestedBy={suggestedBy}
                  suggestedByName={suggestedByName}
                  mode={showWaitingMessage ? "display" : "select"}
                  onSelectTime={showWaitingMessage ? undefined : setSelectedTimeFromList}
                  selectedTime={showWaitingMessage ? undefined : selectedTimeFromList}
                  customTitle={showWaitingMessage ? t('suggested_times.your_suggestions', 'Dina föreslagna tider') : undefined}
                />
              </>
            )}

            {/* Action buttons for time selection - only show when user can actually select */}
            {times.length > 0 && !showWaitingMessage && (coordinationState === 'decision_suggested_times' || coordinationState === 'reader_suggested_times' || coordinationState === 'waiting_for_reader_response') && (
              <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                {/* Show confirm button when a time is selected */}
                {selectedTimeFromList && (
                  <Button
                    variant="contained"
                    onClick={async () => {
                      // Submit the selected time directly and mark it as decided
                      try {
                        if (presentation?.presentationId) {
                          // Save the selected time as the decision and mark it as decided
                          await updateDecision({
                            variables: {
                              presentationId: presentation.presentationId,
                              decision: true,
                              motivation: null,
                              suggestedTimes: [selectedTimeFromList]
                            },
                            refetchQueries: [
                              {
                                query: UserPresentationByTokenQuery,
                                variables: { token: signedToken }
                              }
                            ]
                          });
                          
                          track('accept_with_time_selected_direct', { 
                            presentationId: presentation.presentationId,
                            selectedTime: selectedTimeFromList,
                            signedToken
                          });
                          
                          // Format the selected time for URL
                          const selectedDate = new Date(selectedTimeFromList);
                          const timeParam = encodeURIComponent(selectedTimeFromList);
                          navigate(`/thank-you?decision=time_decided&selectedTime=${timeParam}`);
                        }
                      } catch (error) {
                        console.error('Error updating decision with selected time:', error);
                      }
                    }}
                    size="large"
                    sx={{ 
                      minWidth: '200px',
                      minHeight: '48px',
                    }}
                  >
                    {t('time_coordination.confirm_selected_time', 'Bekräfta vald tid')}
                  </Button>
                )}
                
                {/* Button for suggesting alternative times */}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedTimeFromList(null); // Clear any selected time when suggesting alternatives
                    setDecision(true);
                    setModalOpen(true);
                  }}
                  size="large"
                  sx={{ 
                    minWidth: '200px',
                    minHeight: '48px',
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.8)',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {t('time_coordination.suggest_different_times', 'Föreslå andra tider')}
                </Button>
              </Box>
            )}

            {/* Action Button for initial time suggestion */}
            {coordinationState === 'both_accepted_no_times' && (
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleTimeCoordination}
                  size="large"
                  sx={{ 
                    minWidth: '200px',
                    minHeight: '48px',
                  }}
                >
                  {t('time_coordination.suggest_times', 'Föreslå tider')}
                </Button>
              </Box>
            )}

            {/* Edit suggestions button when waiting for response */}
            {showWaitingMessage && isViewingOwnSuggestions && (
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDecision(true);
                    setModalOpen(true);
                  }}
                  size="large"
                  sx={{ 
                    minWidth: '200px',
                    borderColor: '#8E603C',
                    color: '#8E603C',
                    '&:hover': { 
                      borderColor: '#76451F',
                      color: '#76451F',
                      backgroundColor: 'rgba(142, 96, 60, 0.05)'
                    }
                  }}
                >
                  {t('time_coordination.edit_my_suggestions', 'Ändra mina förslag')}
                </Button>
              </Box>
            )}

            {/* Relate Logo */}
            <Box sx={{ mt: 6, mb: 6, display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/logo.png" 
                alt="Relate" 
                style={{ 
                  height: '40px',
                  opacity: 0.8
                }} 
              />
            </Box>
          </Container>
        </Body>

        {/* Time Selection Modals */}
        <MotivationModal
          open={modalOpen}
          onClose={handleMotivationCancel}
          onSubmit={handleMotivationSubmit}
          decision={decision !== null ? decision : true}
          isTimeOnlyMode={true}
          blockedTimes={[...rejectedTimes, ...decisionTimes]}
          // Clear motivation if decision type changed (accept->reject or reject->accept)
          initialMotivation={readerHasDecided && readerCurrentDecision === decision ? readerMotivation || '' : ''}
          // Keep suggested times only if changing from accept to accept (editing times)
          initialSuggestedTimes={readerHasDecided && readerCurrentDecision && decision ? readerSuggestedTimes : []}
        />

        <TimeSelectionModal
          open={timeSelectionModalOpen}
          onClose={() => setTimeSelectionModalOpen(false)}
          onSubmit={handleTimeSelection}
          existingSuggestedTimes={
            coordinationState === 'decision_suggested_times' ? decisionTimes :
            coordinationState === 'reader_suggested_times' ? readerTimes :
            [...decisionTimes, ...readerTimes] // fallback for complex scenarios
          }
          suggestedBy={
            coordinationState === 'decision_suggested_times' ? 'decision' :
            coordinationState === 'reader_suggested_times' ? 'reader' :
            'decision' // fallback
          }
          suggestedByName={presentation?.firstName}
          blockedTimes={[...rejectedTimes, ...decisionTimes]}
        />
      </>
    );
  }

  // Regular full profile view
  return (
    <>
      <Helmet>
        <title>{t('profile_summary.title', 'Profilöversikt')} - Relate</title>
        <meta name="description" content={t('profile_summary.description', 'Visa din profilöversikt')} />
      </Helmet>
      
      <Body>
        <Container maxWidth="md" sx={{ py: 4 }}>
          
          {/* Profile Photo Gallery */}
          <ProfilePhotoGallery pictures={profilePictures} name={fullName} />

          {/* Name and Basic Info Chips */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h1" gutterBottom sx={{ color: 'white', mb: 2 }}>
              {fullName || t('common.anonymous', 'Anonym')}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {presentation?.age && (
                <Chip
                  label={`${presentation.age} ${t('common.years_old', 'år')}`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '1rem',
                    height: '32px'
                  }}
                />
              )}
              {height && (
                <Chip
                  label={height}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '1rem',
                    height: '32px'
                  }}
                />
              )}
              {presentation?.city && (
                <Chip
                  label={presentation.city}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '1rem',
                    height: '32px'
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Profile Summary from Presentation */}
          <Box sx={{ color: 'white', mb: 6 }}>
            <Typography 
              variant="body1" 
              component="div"
              sx={{ 
                lineHeight: 1.8, 
                fontSize: '1.1rem',
                textAlign: 'left',
                '& p': { mb: 2 },
                '& strong': { fontWeight: 'bold' },
                '& em': { fontStyle: 'italic' },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 0.5 }
              }}
              dangerouslySetInnerHTML={{
                __html: presentation?.summary || t('common.no_summary', 'Ingen sammanfattning tillgänglig.')
              }}
            />
          </Box>


          {/* Decision Buttons */}
          <Box sx={{ mt: 6, mb: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <Typography variant="h4" sx={{ color: 'white', textAlign: 'center', mb: 2 }}>
              {readerHasDecided 
                ? t('profile_summary.change_decision', 'Ändra ditt beslut om') 
                : t('profile_summary.meet_question', 'Vill du träffa')
              } {presentation?.firstName}?
            </Typography>
            
            {readerHasDecided && (
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', mb: 2 }}>
                {readerCurrentDecision 
                  ? t('profile_summary.current_decision_yes', 'Du har tidigare sagt ja')
                  : t('profile_summary.current_decision_no', 'Du har tidigare sagt nej')
                }
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexDirection: 'row' }}>
              <Button
                variant="contained"
                onClick={() => handleDecision(true)}
                size="large"
                sx={{ 
                  minWidth: '160px',
                  minHeight: '48px',
                  flex: { xs: 1, sm: 'none' },
                  // Gold color for yes when: no decision made (default) OR when yes was selected
                  bgcolor: (!readerHasDecided || readerCurrentDecision) ? '#8E603C' : 'rgba(255,255,255,0.1)',
                  color: (!readerHasDecided || readerCurrentDecision) ? 'white' : 'white',
                  '&:hover': { 
                    bgcolor: (!readerHasDecided || readerCurrentDecision) ? '#76451F' : 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                {t('common.yes', 'Ja')}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDecision(false)}
                size="large"
                sx={{ 
                  minWidth: '160px',
                  minHeight: '48px',
                  flex: { xs: 1, sm: 'none' },
                  // Gold color for no only when no was explicitly selected
                  bgcolor: (readerHasDecided && !readerCurrentDecision) ? '#8E603C' : 'rgba(255,255,255,0.1)',
                  color: (readerHasDecided && !readerCurrentDecision) ? 'white' : 'white',
                  '&:hover': { 
                    bgcolor: (readerHasDecided && !readerCurrentDecision) ? '#76451F' : 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                {t('common.no', 'Nej')}
              </Button>
            </Box>
          </Box>

          {/* Relate Logo */}
          <Box sx={{ mt: 6, mb: 6, display: 'flex', justifyContent: 'center' }}>
            <img 
              src="/logo.png" 
              alt="Relate" 
              style={{ 
                height: '40px',
                opacity: 0.8
              }} 
            />
          </Box>
        </Container>
      </Body>

      {/* Motivation Modal */}
      <MotivationModal
        open={modalOpen}
        onClose={handleMotivationCancel}
        onSubmit={handleMotivationSubmit}
        decision={decision !== null ? decision : true}
        // Clear motivation if decision type changed (accept->reject or reject->accept)
        initialMotivation={readerHasDecided && readerCurrentDecision === decision ? readerMotivation || '' : ''}
        // Keep suggested times only if changing from accept to accept (editing times)
        initialSuggestedTimes={readerHasDecided && readerCurrentDecision && decision ? readerSuggestedTimes : []}
      />

      {/* Match Celebration Dialog */}
      <MatchCelebrationDialog
        open={showMatchCelebration}
        onClose={() => setShowMatchCelebration(false)}
        onContinue={handleMatchCelebrationContinue}
        otherPersonName={presentation?.firstName}
      />

      {/* Time Selection Modal for second approval */}
      <TimeSelectionModal
        open={timeSelectionModalOpen}
        onClose={() => {
          setTimeSelectionModalOpen(false);
          setDecision(null); // Clear the stored decision if they cancel
        }}
        onSubmit={handleTimeSelection}
        existingSuggestedTimes={
          // Show the times from whoever has already accepted
          presentation?.decision?.accept && (presentation?.decision?.suggestedTimes?.length || 0) > 0
            ? (presentation?.decision?.suggestedTimes || [])
            : (presentation?.readerDecision?.suggestedTimes || [])
        }
        suggestedBy={
          // Determine who suggested the times
          presentation?.decision?.accept && (presentation?.decision?.suggestedTimes?.length || 0) > 0
            ? 'decision'
            : 'reader'
        }
        suggestedByName={presentation?.firstName}
        blockedTimes={[...(presentation?.rejectedTimes || []), ...(presentation?.decision?.suggestedTimes || [])]}
      />
    </>
  );
} 
